
import React, { useState } from 'react';
import { trackEvent } from '../services/analytics';
import SEO from './SEO';
import {
    Check, ArrowRight, Zap, Globe, Shield, BarChart3,
    Users, Cpu, Star, RefreshCw, Lock, ChevronDown, ChevronUp
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    TIERS,
    PRICING_MATRIX,
    SUBSCRIPTION_PLANS,
    VOLUME_STEPS,
    formatPrice,
    formatCPM,
    formatVolume,
    getCPM,
    TierId,
} from '../constants/pricing';

type PricingMode = 'business' | 'agency';

const highlightedVolumes = [60000, 300000, 1000000, 3000000] as const;
const professionalTier = TIERS.find((tier) => tier.id === 'professional');
const expertTier = TIERS.find((tier) => tier.id === 'expert');

const businessPlans = highlightedVolumes.map((volume) => {
    const price = PRICING_MATRIX.professional[volume][1];

    return {
        name: professionalTier?.name ?? 'Professional',
        traffic: formatVolume(volume),
        price: formatPrice(price),
        priceNote: 'single credit pack',
        desc: `${professionalTier?.quality ?? 'Residential IPs'} · ${professionalTier?.targetingProfile ?? 'Residential Targeting Profile'}`,
        cpm: formatCPM(getCPM('professional', volume)),
        featured: volume === 1000000,
        features: professionalTier?.features ?? [],
    };
});

const agencyPlans = SUBSCRIPTION_PLANS
    .filter((plan) => plan.isAgency)
    .map((plan, index) => ({
        name: plan.name,
        traffic: formatVolume(plan.visitors),
        price: formatPrice(plan.price),
        period: '/month',
        featured: index === 0,
        badge: index === 0 ? 'Agency' : 'White label',
        features: plan.features,
    }));

const featureIcons = [Shield, Globe, BarChart3, Cpu, Users, Zap, RefreshCw, Lock];
const trustBadges = TIERS.flatMap((tier) => tier.features).map((label, index) => {
    const Icon = featureIcons[index % featureIcons.length];
    return { icon: <Icon className="w-5 h-5" />, label };
});

const lowestProfessionalPrice = PRICING_MATRIX.professional[VOLUME_STEPS[0]][1];
const lowestExpertPrice = PRICING_MATRIX.expert[VOLUME_STEPS[0]][1];
const allSinglePackPrices = TIERS.flatMap((tier) =>
    VOLUME_STEPS.map((volume) => PRICING_MATRIX[tier.id][volume][1]),
);

const faqs = [
    {
        q: 'How much does website traffic cost?',
        a: `The minimum pack contains ${formatVolume(VOLUME_STEPS[0])} visits. Professional starts at ${formatPrice(lowestProfessionalPrice)} and Expert starts at ${formatPrice(lowestExpertPrice)}. Every price on this page is shown in USD and comes directly from the current credit-pack matrix.`,
    },
    {
        q: 'Do I need a subscription?',
        a: 'Yes — card payments are set up as a monthly subscription that renews at the displayed price until you cancel, and you can cancel at any time from your account. The displayed prices use the single-pack column of the current pricing matrix. Credits you have already purchased never expire.',
    },
    {
        q: 'What is included with Professional?',
        a: `${professionalTier?.name ?? 'Professional'} includes ${(professionalTier?.features ?? []).join(', ')}. Its current quality profile is ${professionalTier?.quality ?? 'Residential IPs'}.`,
    },
    {
        q: 'What is included with Expert?',
        a: `${expertTier?.name ?? 'Expert'} includes ${(expertTier?.features ?? []).join(', ')}. Its current quality profile is ${expertTier?.quality ?? 'Premium IPs'}.`,
    },
    {
        q: 'Can I target specific countries or cities?',
        a: 'Professional includes country-level targeting. Expert includes city and state targeting, as defined in the current tier configuration.',
    },
    {
        q: 'Which traffic volumes can I buy?',
        a: `Current packs range from ${formatVolume(VOLUME_STEPS[0])} to ${formatVolume(VOLUME_STEPS[VOLUME_STEPS.length - 1])} visits. The same ${VOLUME_STEPS.length} volume steps are available for Professional and Expert.`,
    },
    {
        q: 'Is Economy still available for new purchases?',
        a: 'No. New purchases are available only in Professional and Expert. Economy remains in the system solely for legacy projects and is not displayed as a purchasable tier.',
    },
    {
        q: 'Does Expert include GA4 features?',
        a: 'Yes. The current Expert configuration includes GA4 Natural Events. Actual analytics reporting still depends on the website and analytics setup used for the campaign.',
    },
];

const comparisonRows: { feature: string; us: React.ReactNode; them: React.ReactNode }[] = [
    { feature: 'Network', us: professionalTier?.features[0], them: expertTier?.features[0] },
    { feature: 'Traffic Quality', us: professionalTier?.features[1], them: expertTier?.features[1] },
    { feature: 'Geo Targeting', us: professionalTier?.features[2], them: expertTier?.features[2] },
    { feature: 'Additional Feature', us: professionalTier?.features[3], them: expertTier?.features[3] },
];

const productOffers = TIERS.flatMap((tier) =>
    VOLUME_STEPS.map((volume) => ({
        '@type': 'Offer',
        name: `${tier.name} · ${formatVolume(volume)} visits`,
        price: PRICING_MATRIX[tier.id][volume][1].toFixed(2),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: 'https://traffic-creator.com/pricing',
    })),
);

const agencySubscriptionOffers = SUBSCRIPTION_PLANS
    .filter((plan) => plan.isAgency)
    .map((plan) => ({
        '@type': 'Offer',
        name: `${plan.name} monthly plan`,
        price: plan.price.toFixed(2),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: 'https://traffic-creator.com/pricing',
    }));

const PricingPage: React.FC = () => {
    const [pricingMode, setPricingMode] = useState<PricingMode>('business');
    const [selectedTier, setSelectedTier] = useState<TierId>('professional');
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    const toggleFaq = (index: number) => {
        const isOpening = openFaq !== index;
        setOpenFaq(isOpening ? index : null);
        if (isOpening) {
            trackEvent('faq_click', {
                faq_question: faqs[index]?.q,
                faq_index: index,
                page: '/pricing',
            });
        }
    };

    return (
        <div className="min-h-screen bg-[#f3f4f6] font-sans">
            <SEO
                title={`Website Traffic Pricing — Plans from ${formatPrice(lowestProfessionalPrice)} | Traffic Creator`}
                description={`Compare Professional and Expert website traffic credit packs in USD. Packages start at ${formatPrice(lowestProfessionalPrice)} for ${formatVolume(VOLUME_STEPS[0])} visits, with country, city and state targeting options.`}
                keywords="website traffic pricing, traffic creator pricing, professional website traffic, expert traffic tier, website traffic credit packs"
                type="product"
                schema={[
                    {
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "Traffic Creator Website Traffic Credit Packs",
                        "description": `Professional and Expert website traffic credit packs from ${formatVolume(VOLUME_STEPS[0])} to ${formatVolume(VOLUME_STEPS[VOLUME_STEPS.length - 1])} visits. Prices are listed in USD.`,
                        "brand": { "@type": "Brand", "name": "Traffic Creator" },
                        "offers": {
                            "@type": "AggregateOffer",
                            "lowPrice": Math.min(...allSinglePackPrices).toFixed(2),
                            "highPrice": Math.max(...allSinglePackPrices).toFixed(2),
                            "priceCurrency": "USD",
                            "offerCount": productOffers.length,
                            "offers": productOffers
                        }
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": faqs.map(f => ({
                            "@type": "Question",
                            "name": f.q,
                            "acceptedAnswer": { "@type": "Answer", "text": f.a }
                        }))
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "name": "Traffic Creator Agency Plans",
                        "serviceType": "Agency traffic management subscriptions",
                        "provider": { "@type": "Organization", "name": "Traffic Creator" },
                        "offers": agencySubscriptionOffers
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://traffic-creator.com" },
                            { "@type": "ListItem", "position": 2, "name": "Pricing", "item": "https://traffic-creator.com/pricing" }
                        ]
                    }
                ]}
            />

            {/* ── Header ── */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <span className="text-2xl font-black text-[#ff5a0a] tracking-tight">TRAFFIC</span>
                        <span className="text-xs font-bold bg-black text-white px-2 py-1 rounded-sm uppercase tracking-wide">Creator</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8 text-sm font-bold text-gray-500">
                        <Link to="/buy-website-traffic" className="hover:text-[#ff5a0a] transition-colors">Buy Traffic</Link>
                        <Link to="/blog" className="hover:text-[#ff5a0a] transition-colors">Blog</Link>
                        <Link to="/about" className="hover:text-[#ff5a0a] transition-colors">About</Link>
                    </nav>
                    <Link to="/login" className="bg-black text-white px-5 py-2 text-xs font-black uppercase tracking-wider hover:bg-[#ff5a0a] transition-all rounded-sm">
                        Dashboard Login
                    </Link>
                </div>
            </header>

            {/* ── Breadcrumb ── */}
            <div className="bg-white border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-6 py-3">
                    <nav className="text-xs text-gray-400 font-medium flex items-center gap-2">
                        <Link to="/" className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] -my-3.5 -mx-2 lg:min-h-0 lg:min-w-0 lg:my-0 lg:mx-0 hover:text-[#ff5a0a] transition-colors">Home</Link>
                        <span>›</span>
                        <span className="text-gray-700 font-bold">Pricing</span>
                    </nav>
                </div>
            </div>

            {/* ── Hero ── */}
            <section className="bg-white py-16 px-6 border-b border-gray-100">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 bg-orange-50 text-[#ff5a0a] border border-orange-100 px-4 py-2 text-xs font-black uppercase tracking-widest mb-6 rounded-full">
                        <Zap size={13} className="fill-[#ff5a0a]" /> Monthly Subscription · Cancel Anytime
                    </div>
                    <h1 className="text-5xl md:text-6xl font-black text-gray-900 uppercase tracking-tight mb-4 leading-tight">
                        Website Traffic<br />
                        <span className="text-[#ff5a0a]">Pricing & Plans</span>
                    </h1>
                    <p className="text-gray-500 text-lg font-medium max-w-2xl mx-auto mb-8">
                        Professional website traffic credits start at <strong className="text-gray-900">{formatPrice(lowestProfessionalPrice)} for {formatVolume(VOLUME_STEPS[0])} visits</strong>. Choose Professional or Expert quality and the targeting profile your campaign needs.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 font-medium">
                        <div className="flex items-center gap-1.5"><Shield size={14} className="text-[#ff5a0a]" /> Professional from {formatPrice(lowestProfessionalPrice)}</div>
                        <div className="flex items-center gap-1.5"><Zap size={14} className="text-[#ff5a0a]" /> Expert from {formatPrice(lowestExpertPrice)}</div>
                        <div className="flex items-center gap-1.5"><BarChart3 size={14} className="text-[#ff5a0a]" /> {VOLUME_STEPS.length} volume options per tier</div>
                        <div className="flex items-center gap-1.5"><Globe size={14} className="text-[#ff5a0a]" /> Country, city and state targeting options</div>
                    </div>
                </div>
            </section>

            {/* Quality tiers */}
            <section className="bg-white py-24 px-6">
                <div className="max-w-6xl mx-auto">
                    <p className="text-xs font-black uppercase tracking-widest text-[#ff5a0a] mb-4 text-center">Current Business Growth tiers</p>
                    <h2 className="text-3xl md:text-5xl font-black text-gray-900 uppercase tracking-tight mb-16 leading-[0.9] text-center">
                        Choose <span className="text-[#ff5a0a]">Professional or Expert.</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {TIERS.map((tier) => (
                            <div key={tier.id} className="bg-white border border-gray-100 p-8 shadow-sm hover:-translate-y-1 transition-all duration-300">
                                <div className="flex items-center justify-between gap-4 mb-6">
                                    <div className="min-w-0">
                                        <div className="font-black text-gray-900 text-base min-[360px]:text-xl uppercase">{tier.name}</div>
                                        <div className="text-xs font-bold tracking-widest text-[#ff5a0a] uppercase">{tier.quality}</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-xs text-gray-400 font-bold uppercase">From</div>
                                        <div className="font-black text-gray-900">{formatPrice(PRICING_MATRIX[tier.id][VOLUME_STEPS[0]][1])}</div>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    {tier.features.map((feature) => (
                                        <div key={feature} className="flex items-center gap-3 text-sm text-gray-600 font-medium">
                                            <Check size={15} className="text-[#ff5a0a] shrink-0" />
                                            {feature}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Trust Badges ── */}
            <section className="bg-white py-6 px-6 border-b border-gray-100">
                <div className="max-w-5xl mx-auto">
                    <div className="flex flex-wrap justify-center gap-3">
                        {trustBadges.map((f, i) => (
                            <div key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-4 py-2 rounded-full text-xs font-bold text-gray-700">
                                <span className="text-[#ff5a0a]">{f.icon}</span>
                                {f.label}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Pricing Plans ── */}
            <section className="py-20 px-6">
                <div className="max-w-7xl mx-auto">

                    {/* Mode Toggle */}
                    <div className="flex justify-center mb-12">
                        <div className="inline-flex bg-white border border-gray-200 p-1 shadow-sm">
                            <button
                                onClick={() => {
                                    setPricingMode('business');
                                    trackEvent('pricing_mode_toggle', { mode: 'business' });
                                }}
                                className={`px-8 py-3 min-h-[44px] lg:min-h-0 text-xs font-black uppercase tracking-widest transition-all ${pricingMode === 'business' ? 'bg-black text-white shadow-md' : 'text-gray-500 hover:text-black'}`}
                            >
                                Business Growth
                            </button>
                            <button
                                onClick={() => {
                                    setPricingMode('agency');
                                    trackEvent('pricing_mode_toggle', { mode: 'agency' });
                                }}
                                className={`px-8 py-3 min-h-[44px] lg:min-h-0 text-xs font-black uppercase tracking-widest transition-all ${pricingMode === 'agency' ? 'bg-[#ff5a0a] text-white shadow-md' : 'text-gray-500 hover:text-[#ff5a0a]'}`}
                            >
                                Agency & Reseller
                            </button>
                        </div>
                    </div>

                    {pricingMode === 'business' ? (
                        <>
                            <p className="text-center text-sm text-gray-500 font-medium mb-10">
                                One-time credit packs in USD. Choose a Professional or Expert tier before checkout.
                            </p>

                            {/* 4 Main Plans */}
                            <div className="py-6 px-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                                {businessPlans.map((p, i) => (
                                    <div
                                        key={i}
                                        className={`border-2 p-8 flex flex-col bg-white relative transition-all duration-300 ${p.featured ? 'border-[#ff5a0a] shadow-2xl scale-105 z-10' : 'border-gray-200 hover:shadow-xl hover:border-gray-300'}`}
                                    >
                                        {p.featured && (
                                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#ff5a0a] text-white px-4 py-1 text-[10px] font-black uppercase tracking-widest shadow-md whitespace-nowrap flex items-center gap-1">
                                                <Star size={10} fill="currentColor" /> Most Popular
                                            </div>
                                        )}
                                        <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3">{p.name}</div>
                                        <div className="text-4xl font-black text-gray-900 mb-1">{p.price}</div>
                                        <div className="text-[10px] font-bold text-gray-400 mb-1">{p.priceNote}</div>
                                        <div className="text-xs font-black text-[#ff5a0a] mb-6">{p.traffic} Visits</div>

                                        <p className="text-xs text-gray-500 font-medium mb-6 leading-relaxed flex-1">{p.desc}</p>

                                        <div className="border-t border-gray-100 pt-5 mb-6 space-y-2.5">
                                            {[...p.features, `CPM: ${p.cpm}`].map((f, j) => (
                                                <div key={j} className="flex items-center gap-2 text-xs text-gray-600 font-medium">
                                                    <Check size={13} className="text-[#ff5a0a] shrink-0" />
                                                    {f}
                                                </div>
                                            ))}
                                        </div>

                                        <Link
                                            to="/login"
                                            className={`w-full py-4 text-[10px] font-black uppercase tracking-widest transition-all text-center ${p.featured ? 'bg-[#ff5a0a] text-white hover:bg-black' : 'bg-gray-900 text-white hover:bg-[#ff5a0a]'}`}
                                        >
                                            Buy {p.traffic} Visits →
                                        </Link>
                                    </div>
                                ))}
                            </div>

                            {/* CPM note */}
                            <p className="text-center text-xs text-gray-400 font-medium mb-16">
                                Prices shown in USD · Single credit-pack pricing · Volume-specific CPM displayed per option
                            </p>

                            {/* Volume × Quality Tier Matrix */}
                            <div className="border-t border-gray-200 pt-16">
                                <div className="text-center mb-8">
                                    <h2 className="text-2xl font-black uppercase tracking-tight text-gray-900 mb-2">
                                        Advanced Volume & Quality Selector
                                    </h2>
                                    <p className="text-gray-500 text-sm font-medium max-w-xl mx-auto">
                                        Choose from {formatVolume(VOLUME_STEPS[0])} to {formatVolume(VOLUME_STEPS[VOLUME_STEPS.length - 1])} visits with Professional or Expert traffic quality.
                                    </p>
                                </div>

                                {/* Tier Selector */}
                                <div className="flex justify-center gap-3 mb-6">
                                    {TIERS.map((tier) => (
                                        <button
                                            key={tier.id}
                                            onClick={() => {
                                                setSelectedTier(tier.id);
                                                trackEvent('pricing_tier_select', {
                                                    tier: tier.id,
                                                    page: '/pricing',
                                                });
                                            }}
                                            className={`px-6 py-3 min-h-[44px] lg:min-h-0 text-xs font-black uppercase tracking-widest transition-all ${selectedTier === tier.id ? 'bg-[#ff5a0a] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#ff5a0a] hover:text-[#ff5a0a]'}`}
                                        >
                                            {tier.name}
                                            {tier.popular && <Star size={10} fill="currentColor" className="ml-1.5 inline" />}
                                        </button>
                                    ))}
                                </div>

                                {/* Tier Features */}
                                <div className="flex flex-wrap justify-center gap-2 mb-8">
                                    {TIERS.find(t => t.id === selectedTier)?.features.map((f, i) => (
                                        <span key={i} className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-200 px-3 py-1.5 rounded-full">
                                            <Check size={11} className="text-[#ff5a0a]" /> {f}
                                        </span>
                                    ))}
                                </div>

                                {/* Volume Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                                    {VOLUME_STEPS.map((volume) => {
                                        const price = PRICING_MATRIX[selectedTier][volume]?.[1] || 0;
                                        const cpm = getCPM(selectedTier, volume, 1);
                                        const isPopular = volume === 600000;
                                        return (
                                            <div
                                                key={volume}
                                                className={`bg-white p-6 border-2 flex flex-col relative transition-all ${isPopular ? 'border-[#ff5a0a] shadow-xl scale-105' : 'border-gray-200 hover:border-[#ff5a0a]/40 hover:shadow-md'}`}
                                            >
                                                {isPopular && (
                                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ff5a0a] text-white text-[8px] font-black uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap">
                                                        Popular
                                                    </div>
                                                )}
                                                <div className="text-center mb-3">
                                                    <div className="text-2xl font-black text-gray-900">
                                                        {volume >= 1000000 ? `${volume / 1000000}M` : `${volume / 1000}k`}
                                                    </div>
                                                    <div className="text-[9px] text-gray-400 font-black uppercase tracking-widest">Visitors</div>
                                                </div>
                                                <div className="text-center mb-4">
                                                    <div className="text-xl font-black text-gray-900">{formatPrice(price)}</div>
                                                    <div className="text-[9px] text-[#ff5a0a] font-bold">{formatCPM(cpm)}/1k visits</div>
                                                </div>
                                                <Link
                                                    to="/login"
                                                    className={`w-full py-2.5 min-h-[44px] lg:min-h-0 flex items-center justify-center text-[9px] font-black uppercase tracking-widest text-center mt-auto transition-all ${isPopular ? 'bg-[#ff5a0a] text-white hover:bg-black' : 'bg-gray-900 text-white hover:bg-[#ff5a0a]'}`}
                                                >
                                                    Buy Now
                                                </Link>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="text-center text-sm text-gray-500 font-medium mb-10">
                                Monthly traffic pools for agencies and resellers. White-label available. Cancel anytime.
                            </p>
                            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                                {agencyPlans.map((p, i) => (
                                    <div
                                        key={i}
                                        className={`border-2 p-6 sm:p-10 flex flex-col bg-white relative transition-all duration-300 ${p.featured ? 'border-[#ff5a0a] shadow-2xl z-10 md:scale-105' : 'border-gray-200 hover:shadow-xl hover:border-gray-300'}`}
                                    >
                                        {p.badge && (
                                            <div className="absolute top-0 right-0 bg-[#ff5a0a] text-white px-4 py-2 text-[10px] font-black uppercase tracking-widest">
                                                {p.badge}
                                            </div>
                                        )}
                                        <h3 className="text-lg font-black uppercase tracking-tight text-gray-900 mb-2">{p.name}</h3>
                                        <div className="flex flex-wrap items-baseline gap-1 mb-1">
                                            <span className="text-3xl sm:text-4xl font-black text-gray-900">{p.price}</span>
                                            {p.period && <span className="text-sm text-gray-400 font-medium">{p.period}</span>}
                                        </div>
                                        <div className="text-sm font-bold text-gray-500 mb-8 border-b border-gray-100 pb-4">
                                            Monthly Traffic Pool: <span className="text-black">{p.traffic}</span>
                                        </div>
                                        <ul className="space-y-3.5 mb-10 flex-1">
                                            {p.features.map((f, j) => (
                                                <li key={j} className="text-sm text-gray-600 flex items-start gap-3 font-medium">
                                                    <Check size={15} className="text-[#ff5a0a] shrink-0 mt-0.5" />
                                                    {f}
                                                </li>
                                            ))}
                                        </ul>
                                        <Link
                                            to={p.price === 'Custom' ? '/helpdesk' : '/signup'}
                                            className="w-full bg-black text-white py-4 text-xs font-black uppercase tracking-widest hover:bg-[#ff5a0a] transition-colors shadow-lg text-center"
                                        >
                                            {p.price === 'Custom' ? 'Contact Sales' : 'Join Agency Program'}
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </section >

            {/* ── Comparison Table ── */}
            < section className="py-20 px-6 bg-white border-t border-gray-100" >
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-2xl font-black uppercase tracking-tight text-gray-900 mb-2 text-center">
                        Compare Traffic Quality Tiers
                    </h2>
                    <p className="text-center text-sm text-gray-500 font-medium mb-10">The current Professional and Expert features, side by side.</p>
                    <div className="overflow-hidden border border-gray-200 bg-white">
                        <div className="grid grid-cols-3 gap-2 sm:gap-0 bg-gray-900 text-white px-4 sm:px-6 py-4 text-[10px] sm:text-xs font-black uppercase tracking-normal sm:tracking-widest">
                            <div className="break-words">Feature</div>
                            <div className="text-[#ff5a0a] break-words">{professionalTier?.name ?? 'Professional'}</div>
                            <div className="text-gray-300 break-words">{expertTier?.name ?? 'Expert'}</div>
                        </div>
                        {comparisonRows.map((row, i) => (
                            <div key={i} className={`grid grid-cols-3 gap-2 sm:gap-0 px-4 sm:px-6 py-4 text-sm items-center border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <div className="font-bold text-gray-800 text-xs uppercase tracking-wide break-words">{row.feature}</div>
                                <div className="font-bold text-[#ff5a0a] text-xs break-words">{row.us}</div>
                                <div className="text-gray-400 text-xs break-words">{row.them}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section >

            {/* ── FAQ ── */}
            < section className="py-20 px-6 bg-[#f3f4f6] border-t border-gray-200" >
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-2xl font-black uppercase tracking-tight text-gray-900 mb-2 text-center">
                        Frequently Asked Questions
                    </h2>
                    <p className="text-center text-sm text-gray-500 font-medium mb-10">
                        Everything you need to know about our pricing and traffic quality.
                    </p>
                    <div className="space-y-3">
                        {faqs.map((item, i) => (
                            <div key={i} className="bg-white border border-gray-200 overflow-hidden">
                                <button
                                    className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
                                    onClick={() => toggleFaq(i)}
                                >
                                    <span className="font-black text-sm text-gray-900 pr-4 uppercase tracking-tight">{item.q}</span>
                                    {openFaq === i
                                        ? <ChevronUp size={18} className="text-[#ff5a0a] shrink-0" />
                                        : <ChevronDown size={18} className="text-gray-400 shrink-0" />
                                    }
                                </button>
                                {openFaq === i && (
                                    <div className="px-6 pb-5 text-sm text-gray-600 font-medium leading-relaxed border-t border-gray-100 pt-4">
                                        {item.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section >

            {/* ── Internal Links / Content ── */}
            < section className="py-16 px-6 bg-white border-t border-gray-100" >
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-900 mb-6 text-center">
                        Learn More About Website Traffic
                    </h2>
                    <div className="grid sm:grid-cols-3 gap-4">
                        {[
                            { to: '/blog/best-traffic-bot-2026', label: 'Best Traffic Bots 2026', desc: 'Independent comparison of top traffic tools.' },
                            { to: '/blog/buy-seo-traffic', label: 'How to Buy SEO Traffic', desc: 'Guide to choosing the right traffic for your site.' },
                            { to: '/buy-website-traffic', label: 'Buy Website Traffic', desc: 'Our main service page with full feature details.' },
                        ].map((link, i) => (
                            <Link key={i} to={link.to} className="group border border-gray-200 p-5 hover:border-[#ff5a0a] hover:shadow-md transition-all bg-gray-50 hover:bg-white">
                                <div className="text-xs font-black uppercase tracking-widest text-gray-900 group-hover:text-[#ff5a0a] transition-colors mb-1">{link.label}</div>
                                <div className="text-xs text-gray-500 font-medium">{link.desc}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section >

            {/* ── CTA ── */}
            < section className="py-20 px-6 bg-black text-white" >
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-4xl font-black uppercase tracking-tight mb-4">Ready to Drive Traffic?</h2>
                    <p className="text-gray-400 font-medium mb-10">
                        Create an account and choose a Professional or Expert credit pack. No Business Growth subscription is required.
                    </p>
                    <Link
                        to="/signup"
                        className="inline-flex items-center gap-3 bg-[#ff5a0a] text-white px-10 py-5 text-sm font-black uppercase tracking-widest hover:bg-white hover:text-black transition-all shadow-[0_20px_40px_rgba(255,90,10,0.3)]"
                    >
                        Get Started — From {formatPrice(lowestProfessionalPrice)} <ArrowRight size={18} />
                    </Link>
                    <p className="text-gray-600 text-xs font-bold uppercase tracking-widest mt-6">
                        Prices in USD · {formatVolume(VOLUME_STEPS[0])} minimum · Professional and Expert tiers
                    </p>
                </div>
            </section >

            {/* ── Footer ── */}
            < footer className="bg-black border-t border-gray-900 py-8 px-6" >
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-600 font-medium">
                    <div>© {new Date().getFullYear()} EasyTrafficBot UG (haftungsbeschränkt) · Amtsgericht Wuppertal, HRB 30863</div>
                    <div className="flex gap-6">
                        <Link to="/" className="hover:text-[#ff5a0a] transition-colors">Home</Link>
                        <Link to="/buy-website-traffic" className="hover:text-[#ff5a0a] transition-colors">Buy Traffic</Link>
                        <Link to="/blog" className="hover:text-[#ff5a0a] transition-colors">Blog</Link>
                        <Link to="/about" className="hover:text-[#ff5a0a] transition-colors">About</Link>
                        <Link to="/legal/privacy" className="hover:text-[#ff5a0a] transition-colors">Privacy</Link>
                        <Link to="/legal/terms" className="hover:text-[#ff5a0a] transition-colors">Terms</Link>
                    </div>
                </div>
            </footer >
        </div >
    );
};

export default PricingPage;
